import { FormattedMessage, useIntl } from 'react-intl'
import { useBlueprintColumns } from '@hooks/useQuery'
import type { SortDirection, SortNode } from 'src/types/queries'

import './SortEditor.css'

interface SortEditorProps {
  node: SortNode
  onChange: (node: SortNode) => void
}

export default function SortEditor({ node, onChange }: SortEditorProps) {
  const intl = useIntl()

  const { colSpec = [] } = useBlueprintColumns(
    node.source,
    `cols/${node.source.nodeId}`
  )

  const handleColumnChange = (index: number, columnName: string) => {
    const newCols = node.cols.map((condition, i) =>
      i === index ? { ...condition, col: columnName } : condition
    )
    onChange({ ...node, cols: newCols })
  }

  const handleDirectionChange = (index: number, direction: SortDirection) => {
    const newCols = node.cols.map((condition, i) =>
      i === index ? { ...condition, direction } : condition
    )
    onChange({ ...node, cols: newCols })
  }

  const handleAddSort = () => {
    onChange({ ...node, cols: [...node.cols, { col: '', direction: 'asc' }] })
  }

  const handleRemoveSort = (index: number) => {
    const newCols = node.cols.filter((_, i) => i !== index)
    onChange({ ...node, cols: newCols })
  }

  const canRemove = node.cols.length > 1

  return (
    <div id="sort-editor">
      <header>
        <h3>
          <FormattedMessage id={`operation-editor.option.${node.type}`} />
        </h3>
      </header>

      <div className="columns-title">
        <h4>
          <FormattedMessage id="sort-editor.column.label" />
        </h4>
        <h4>
          <FormattedMessage id="sort-editor.direction.label" />
        </h4>
      </div>

      <ul className="columns-list">
        {node.cols.map((condition, index) => {
          const otherSelectedCols = node.cols
            .filter((_, i) => i !== index)
            .map((c) => c.col)

          return (
            <li key={index} className="column">
              <select
                className="col-select"
                value={condition.col}
                onChange={(e) => handleColumnChange(index, e.target.value)}
              >
                <option disabled hidden value="">
                  {intl.formatMessage({
                    id: 'select-editor.column.placeholder'
                  })}
                </option>
                {colSpec.map((c, idx) => (
                  <option
                    key={`${c.name}-${idx}`}
                    value={c.name}
                    disabled={otherSelectedCols.includes(c.name)}
                  >
                    {c.name}
                  </option>
                ))}
              </select>
              <select
                className="direction-select"
                value={condition.direction}
                disabled={!condition.col}
                onChange={(e) =>
                  handleDirectionChange(index, e.target.value as SortDirection)
                }
              >
                <option value="asc">
                  {intl.formatMessage({ id: 'sort-editor.direction.asc' })}
                </option>
                <option value="desc">
                  {intl.formatMessage({ id: 'sort-editor.direction.desc' })}
                </option>
              </select>
              <button
                type="button"
                className="remove-column"
                onClick={() => handleRemoveSort(index)}
                disabled={!canRemove}
              >
                <span className="icon icon-bin" />
              </button>
            </li>
          )
        })}
      </ul>
      <button
        type="button"
        className="add-column"
        onClick={handleAddSort}
        disabled={node.cols.length >= colSpec.length}
      >
        <span className="icon icon-plus" />
      </button>
    </div>
  )
}
